import type { components } from './schema';

/*
 * Error normalization (spec §3.3, §4.3).
 * Every failure becomes an `ApiError`. It stays a class so `instanceof` works
 * in error components and form handlers.
 */

type Problem = components['schemas']['Problem'];

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly url: string;
  readonly fieldErrors: FieldError[] | undefined;
  /** Parsed `Retry-After` in seconds, when the response carried one (e.g. 429). */
  readonly retryAfter: number | undefined;
  /**
   * The raw Problem Details body, retained so a transport that needs a
   * route-specific extension field off the body (beyond the normalized
   * status/code/detail/errors) can read it without the normalizer having to
   * know every route's shape. Generic and read-only: `status`, `code`,
   * `message` and `fieldErrors` remain the normalized surface every caller
   * uses; `body` is the escape hatch for the rare typed extension (e.g. F02's
   * deactivated-collision profile on a 409, CR-E01-13), read at a transport
   * boundary and narrowed there, never threaded raw into UI. Undefined when the
   * failure carried no JSON body.
   */
  readonly body: unknown;

  constructor(params: {
    status: number;
    code: string;
    message: string;
    url: string;
    fieldErrors?: FieldError[];
    retryAfter?: number;
    body?: unknown;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code;
    this.url = params.url;
    this.fieldErrors = params.fieldErrors;
    this.retryAfter = params.retryAfter;
    // `body` retains the raw Problem Details so a transport can narrow a
    // route-specific extension off it (deactivatedCollisionFromError,
    // unrecognizedKeysFromError). It is defined NON-ENUMERABLE so it never
    // reaches telemetry: the RUM adapter serializes an Error's own enumerable
    // properties, and `body` may carry PII (a deactivated profile's
    // displayName, CR-E01-13). A non-enumerable property is still readable by
    // name at the transport boundary (`error.body`) but is excluded from
    // JSON.stringify / property enumeration, so it cannot leak into a recorded
    // error payload regardless of how RUM serializes (finding 15).
    Object.defineProperty(this, 'body', {
      value: params.body,
      enumerable: false,
      writable: false,
      configurable: false,
    });
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isValidation(): boolean {
    return this.status === 422;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Build an ApiError from a Problem Details body (or a bare failure). */
export function toApiError(
  status: number,
  url: string,
  body: unknown,
  retryAfter?: number,
): ApiError {
  const problem = (body ?? {}) as Partial<Problem>;
  const fieldErrors = Array.isArray(problem.errors)
    ? problem.errors.map((e) => ({ field: e.field, message: e.message }))
    : undefined;

  return new ApiError({
    status,
    code: problem.code ?? `http_${status}`,
    message: problem.detail ?? problem.title ?? `Request failed (${status})`,
    url,
    ...(fieldErrors ? { fieldErrors } : {}),
    ...(retryAfter !== undefined ? { retryAfter } : {}),
    // Keep the raw body so a transport can narrow a route-specific extension
    // off it (the normalized fields above stay the common surface).
    body,
  });
}

/*
 * Parse an HTTP `Retry-After` header into a non-negative delay in SECONDS.
 * The header is either a delay in delta-seconds ("120") or an HTTP-date
 * ("Wed, 21 Oct 2026 07:28:00 GMT"), per RFC 9110 §10.2.3. Returns undefined
 * for an absent, malformed, or negative value so callers fall back to backoff.
 */
export function parseRetryAfter(
  header: string | null,
  now: number = Date.now(),
): number | undefined {
  if (header == null) return undefined;
  const trimmed = header.trim();
  if (trimmed === '') return undefined;

  // Delta-seconds form (integer only).
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number(trimmed);
    return Number.isFinite(seconds) ? seconds : undefined;
  }

  // HTTP-date form. Every HTTP-date carries a day/month name, so require an
  // alphabetic character before trusting Date.parse — this rejects otherwise
  // date-parseable junk like a bare "12.5" (which Date.parse reads as a year).
  if (!/[a-z]/i.test(trimmed)) return undefined;
  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) return undefined;
  const seconds = (dateMs - now) / 1000;
  return seconds >= 0 ? seconds : 0;
}

/*
 * Auth-route allowlist (spec §3.5). Expected auth-flow 4xx (e.g. a failed
 * login) are exempt from the global 401/403 handlers so they don't trigger a
 * redirect storm; the calling code handles them locally.
 *
 * Match on the URL's PATHNAME, never a raw-string substring of the whole URL:
 * a raw match would misclassify a genuine route's 4xx whose QUERY STRING
 * happened to contain an auth path, e.g. `/invoices?next=/auth/session` —
 * suppressing a real redirect. The API base is relative (`/api`), so the real
 * request pathname is `/api/auth/session`; match the allowlisted route as a
 * path SUFFIX (leading `/` enforces a segment boundary) so the base prefix is
 * tolerated while the query string is excluded. Fall back to the raw string
 * only for a relative URL that `URL` cannot parse without a base.
 */
const AUTH_ROUTES = ['/auth/session', '/auth/sign-out', '/sign-in'];

function pathnameOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    // Relative URL (no origin); strip any query/hash and match on the path.
    return url.split('?')[0]?.split('#')[0] ?? url;
  }
}

export function isAuthRoute(url: string): boolean {
  const pathname = pathnameOf(url);
  return AUTH_ROUTES.some(
    (route) => pathname === route || pathname.endsWith(route),
  );
}
